import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Calendar, User, ArrowRight, Plus, Edit, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import ParticleBackground from '../components/ParticleBackground';
import { useSupabaseNews } from '../hooks/useSupabaseNews';

interface Article {
  id: string;
  title: { [key: string]: string };
  excerpt: { [key: string]: string };
  content: { [key: string]: string };
  date: string;
  image: string;
  category: string;
}

export default function NewsPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { news: articles, loading, error, addNews: addNewsToDb, updateNews: updateNewsInDb, deleteNews: deleteNewsFromDb } = useSupabaseNews();
  const [showNewsManager, setShowNewsManager] = useState(false);

  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true' ||
                  localStorage.getItem('admin_logged_in') === 'true';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron mb-6">
              {t('navigation.news')}
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {t('news.subtitle')}
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">Načítání novinek...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-400 text-lg">Chyba při načítání novinek: {error}</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">Žádné novinky k zobrazení</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl overflow-hidden hover:border-red-400/40 transition-all hover:scale-105"
                >
                  <div className="aspect-video bg-gray-800 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title[i18n.language] || article.title.cs}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-gray-400 text-sm mb-4">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{new Date(article.date).toLocaleDateString('cs-CZ')}</span>
                    </div>
                    <h2 className="text-xl font-bold text-white mb-3">
                      {article.title[i18n.language] || article.title.cs}
                    </h2>
                    <p className="text-gray-400 mb-4">
                      {article.excerpt[i18n.language] || article.excerpt.cs}
                    </p>
                    <button className="flex items-center text-[#EE1C23] hover:text-red-300 transition-colors">
                      <span>Číst více</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>
          )}

          {/* Admin Add News Button */}
          {isAdmin && !loading && (
            <div className="flex justify-center mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowNewsManager(true)}
                className="bg-orange-600 hover:bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Spravovat novinky</span>
              </motion.button>
            </div>
          )}
        </div>
      </section>

      {/* News Manager */}
      {showNewsManager && (
        <NewsManager
          onClose={() => setShowNewsManager(false)}
          themeColor="#EE1C23"
          news={articles}
          onAddNews={addNewsToDb}
          onUpdateNews={updateNewsInDb}
          onDeleteNews={deleteNewsFromDb}
        />
      )}
    </div>
  );
}

// News Manager Component
interface NewsManagerProps {
  onClose: () => void;
  themeColor: string;
  news: Article[];
  onAddNews: (newsData: any) => Promise<any>;
  onUpdateNews: (id: string, newsData: any) => Promise<any>;
  onDeleteNews: (id: string) => Promise<void>;
}

function NewsManager({ onClose, themeColor, news: initialNews, onAddNews, onUpdateNews, onDeleteNews }: NewsManagerProps) {
  const [news, setNews] = useState<Article[]>(initialNews);
  const [saving, setSaving] = useState(false);

  const addNewsItem = () => {
    const newArticle = {
      id: Date.now().toString(),
      title: { cs: '', en: '', de: '', sk: '' },
      excerpt: { cs: '', en: '', de: '', sk: '' },
      content: { cs: '', en: '', de: '', sk: '' },
      date: new Date().toISOString().split('T')[0],
      image: '/-ct_logo_light copy copy.png',
      category: 'product-launch'
    };
    setNews([...news, newArticle]);
  };

  const updateNewsItem = (id: string, field: string, value: any, lang?: string) => {
    setNews(prev =>
      prev.map(article => {
        if (article.id === id) {
          if (lang) {
            return { ...article, [field]: { ...article[field], [lang]: value } };
          } else {
            return { ...article, [field]: value };
          }
        }
        return article;
      })
    );
  };

  const deleteNewsItem = (id: string) => {
    setNews(prev => prev.filter(article => article.id !== id));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      for (const article of news) {
        const articleInInitialNews = initialNews.find(n => n.id === article.id);

        if (!articleInInitialNews) {
          await onAddNews(article);
        } else {
          const hasChanges = JSON.stringify(article) !== JSON.stringify(articleInInitialNews);
          if (hasChanges) {
            await onUpdateNews(article.id, article);
          }
        }
      }

      for (const article of initialNews) {
        const articleInNews = news.find(n => n.id === article.id);
        if (!articleInNews) {
          await onDeleteNews(article.id);
        }
      }

      const notification = document.createElement('div');
      notification.innerHTML = `
        <div style="
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        ">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Novinky byly úspěšně uloženy
        </div>
      `;

      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        pointer-events: none;
        transform: translateX(100%);
        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.transform = 'translateX(0)';
      }, 100);

      setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
      }, 2500);

      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      onClose();
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Chyba při ukládání novinek: ' + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-red-500/30 rounded-2xl p-8 w-[90vw] max-w-6xl h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl font-bold text-white">Správa novinek</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6 mb-6">
          {news.map((article) => (
            <div key={article.id} className="bg-gray-800 p-6 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  value={article.title.cs}
                  onChange={(e) => updateNewsItem(article.id, 'title', e.target.value, 'cs')}
                  placeholder="Název (CS)"
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-red-500 focus:outline-none"
                />
                <input
                  type="text"
                  value={article.title.en}
                  onChange={(e) => updateNewsItem(article.id, 'title', e.target.value, 'en')}
                  placeholder="Název (EN)"
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-red-500 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  value={article.title.de || ''}
                  onChange={(e) => updateNewsItem(article.id, 'title', e.target.value, 'de')}
                  placeholder="Název (DE)"
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                />
                <input
                  type="text"
                  value={article.title.sk || ''}
                  onChange={(e) => updateNewsItem(article.id, 'title', e.target.value, 'sk')}
                  placeholder="Název (SK)"
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                />
              </div>
              <textarea
                value={article.excerpt.cs}
                onChange={(e) => updateNewsItem(article.id, 'excerpt', e.target.value, 'cs')}
                placeholder="Krátký popis (CS)"
                rows={2}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-red-500 focus:outline-none mb-4"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <textarea
                  value={article.excerpt.en || ''}
                  onChange={(e) => updateNewsItem(article.id, 'excerpt', e.target.value, 'en')}
                  placeholder="Krátký popis (EN)"
                  rows={2}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                />
                <textarea
                  value={article.excerpt.de || ''}
                  onChange={(e) => updateNewsItem(article.id, 'excerpt', e.target.value, 'de')}
                  placeholder="Krátký popis (DE)"
                  rows={2}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                />
              </div>
              <textarea
                value={article.excerpt.sk || ''}
                onChange={(e) => updateNewsItem(article.id, 'excerpt', e.target.value, 'sk')}
                placeholder="Krátký popis (SK)"
                rows={2}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none mb-4"
              />
              <div className="flex items-center space-x-4">
                <input
                  type="date"
                  value={article.date}
                  onChange={(e) => updateNewsItem(article.id, 'date', e.target.value)}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-red-500 focus:outline-none"
                />
                <button
                  onClick={() => deleteNewsItem(article.id)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex space-x-4">
          <button
            onClick={addNewsItem}
            className="bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Přidat novinku
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors font-semibold"
          >
            Zrušit
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 rounded-lg hover:from-red-400 hover:to-red-500 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Ukládání...' : 'Uložit změny'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}